import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Menu, X, User, LogOut, LayoutDashboard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import NotificationBell from '../Notifications/NotificationBell';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Filter nav links based on user role
  const getNavLinks = () => {
    const baseLinks = [
      { name: 'Accueil', path: '/' },
      { name: 'Offre', path: '/offre' },
      { name: 'Téléchargements', path: '/telechargements' },
      { name: 'Tutoriels et assistance', path: '/tutoriels' },
      { name: 'FAQ', path: '/faq' },
    ];
    
    // Only show "Devenir Vendeur" if not logged in as vendor or admin
    if (!isAuthenticated || (user?.role !== 'vendor' && user?.role !== 'admin')) {
      baseLinks.push({ name: 'Devenir Vendeur', path: '/devenir-vendeur' });
    }
    
    return baseLinks;
  };

  const navLinks = getNavLinks();

  return (
    <nav className="fixed top-0 w-full z-50 glass-morphism-warm border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_0b530233-b14a-4bce-b36f-d5990891e60a/artifacts/hg3eitx4_Logo.png" 
              alt="IPTV4KFR" 
              className="h-32 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-[#E8E8E8]/80 hover:text-[#FF6B35] transition-colors duration-300 font-medium"
              >
                {link.name}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {user?.role === 'admin' && <NotificationBell />}
                <Link
                  to={user?.role === 'admin' ? '/admin' : user?.role === 'vendor' ? '/vendor' : user?.role === 'influencer' ? '/influencer' : '/client'}
                  className="flex items-center space-x-2 text-[#E8E8E8]/80 hover:text-[#FF6B35] transition-colors"
                  data-testid="dashboard-link"
                >
                  <LayoutDashboard className="h-5 w-5" />
                  <span>Dashboard</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-[#E8E8E8]/80 hover:text-[#FF6B35] transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Déconnexion</span>
                </button>
              </>
            ) : (
              <>
                <Link to="/connexion" className="btn-secondary-warm" data-testid="login-button">
                  Connexion
                </Link>
                <Link to="/inscription" className="btn-primary-warm" data-testid="register-button">
                  S'inscrire
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden text-[#E8E8E8]/80 hover:text-[#FF6B35]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-morphism-warm border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-[#E8E8E8]/80 hover:text-[#FF6B35] transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              {isAuthenticated ? (
                <>
                  <Link
                    to={user?.role === 'admin' ? '/admin' : user?.role === 'vendor' ? '/vendor' : user?.role === 'influencer' ? '/influencer' : '/client'}
                    className="block text-[#E8E8E8]/80 hover:text-[#FF6B35] transition-colors py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left text-[#E8E8E8]/80 hover:text-[#FF6B35] transition-colors py-2"
                  >
                    Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/connexion"
                    className="block btn-secondary-warm text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/inscription"
                    className="block btn-primary-warm text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    S'inscrire
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;